import { ReportProblemOutlined } from '@mui/icons-material'
import {
  InputAdornment,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material'
import { GetCurrentLanguageNumberPart } from 'components/Common/Input/NumberParts'
import { useRef } from 'react'
import {
  NumberFormatValues,
  NumericFormat,
  NumericFormatProps,
} from 'react-number-format'

export type DefinitiveDecimalTextFieldProps = {
  value: number | string
  onChange: (value: number) => void
  numericFormatProps?: NumericFormatProps
  textFieldProps?: TextFieldProps
  disableSelectOnFocus?: boolean
  warnings?: string[]
  rootElementClassName?: string
  showRawValueInTooltip?: (value: number | string | '') => string
  disableWarningsIcon?: boolean
  maxValue?: number
  minValue?: number
  isAllowed?: (values: NumberFormatValues) => boolean
  selectTextOnFirstRender?: boolean
  /**
   * if true will call the props.onChange only on blur and enter key press. Default to false
   * It's important if you are formatting the value outside the component,
   * since doing that will cause the caret to move to the end of the input on every render
   * @default false
   */
  changeOnBlur?: boolean
}

const DecimalTextFieldTooltipTitle = (props: {
  warnings: string[]
  rawValue: string | number
}) => {
  return (
    <div>
      {<div>{props.rawValue}</div>}
      {props.warnings?.length > 0 && (
        <div>
          <ul>
            {props.warnings.map((warning, index) => (
              <li key={index}>
                <Typography variant="caption">{warning}</Typography>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function TextFieldWithAutoSelect(props: TextFieldProps) {
  return (
    <TextField
      {...props}
      onFocus={(e) => {
        e.target.select()
      }}
      inputProps={{
        ...props.inputProps,
      }}
    />
  )
}

export const DefinitiveDecimalTextField = (
  props: DefinitiveDecimalTextFieldProps,
) => {
  const showTooltip =
    (props.value && props.value.toString().length > 6) || props.warnings?.length
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Tooltip
      title={
        showTooltip && props.showRawValueInTooltip ? (
          <DecimalTextFieldTooltipTitle
            warnings={props.warnings}
            rawValue={
              props.showRawValueInTooltip
                ? props.showRawValueInTooltip(props.value)
                : props.value
            }
          />
        ) : (
          ''
        )
      }
    >
      <div className={props.rootElementClassName}>
        <NumericFormat
          {...props.numericFormatProps}
          inputRef={inputRef}
          thousandSeparator={
            props.numericFormatProps?.thousandSeparator
              ? GetCurrentLanguageNumberPart('group')
              : false
          }
          decimalSeparator={GetCurrentLanguageNumberPart('decimal')}
          decimalScale={props.numericFormatProps?.decimalScale ?? 2}
          size={props.textFieldProps?.size}
          color={props.textFieldProps?.color}
          error={props.warnings?.length > 0}
          customInput={
            props.disableSelectOnFocus ? TextField : TextFieldWithAutoSelect
          }
          {...(props.textFieldProps as any)}
          helperText={
            props.textFieldProps.helperText
              ? props.textFieldProps.helperText
              : props.warnings?.length > 0 &&
                props.disableWarningsIcon &&
                props.warnings.map((x) => (
                  <Typography
                    key={x}
                    variant={'caption'}
                    component="span"
                    style={{ whiteSpace: 'normal', display: 'block' }}
                  >
                    {x}
                  </Typography>
                ))
          }
          InputProps={{
            ...(props.textFieldProps?.InputProps || {}),
            startAdornment: props.textFieldProps?.InputProps?.startAdornment ? (
              <InputAdornment position="start">
                {props.textFieldProps.InputProps?.startAdornment}
                {props.warnings && !props.disableWarningsIcon && (
                  <Tooltip title={props.warnings.join('\n')}>
                    <span>
                      <ReportProblemOutlined color="secondary" />
                    </span>
                  </Tooltip>
                )}
              </InputAdornment>
            ) : (
              props.warnings &&
              !props.disableWarningsIcon && (
                <InputAdornment position="start">
                  <Tooltip title={props.warnings.join('\n')}>
                    <ReportProblemOutlined color="secondary" />
                  </Tooltip>
                </InputAdornment>
              )
            ),
            endAdornment: props.textFieldProps?.InputProps?.endAdornment && (
              <InputAdornment position="end">
                {props.textFieldProps.InputProps?.endAdornment}
              </InputAdornment>
            ),
          }}
          value={props.value}
          onValueChange={(values, sourceInfo) => {
            if (!props.changeOnBlur) {
              if (sourceInfo.source === 'event') {
                props.onChange(values.floatValue)
              }
            }
          }}
          onBlur={(e) => {
            if (props.changeOnBlur) {
              const newVal = Number(e.target.value)
              if (props.value.toString() !== e.target.value) {
                props.onChange(newVal)
              }
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const newValue = Number(
                e.currentTarget.querySelector('input')?.value,
              )

              if (!isNaN(newValue)) {
                props.onChange(newValue)
              }
            }
          }}
          isAllowed={props.isAllowed}
        />
      </div>
    </Tooltip>
  )
}
